import {
  Id,
  Options,
  Resource,
  SuccessResponse,
  UserAction,
  UserBulkDeleteResponse,
  UserCreateResponse,
  UserGetResponse,
  UserListResponse,
  UserPayload,
  UsersExportResponse,
  UserUploadResponse
} from 'services/api';

import Company from './Company.service';

/**
 * Service for Employee in Comadmin
 */
export default class User extends Company {
  private static instance: User;

  private constructor(options: Options) {
    /* istanbul ignore next */
    super(options);
  }

  /**
   * This return the singleton instance of the class
   * @param {Options} options
   */
  public static getInstance(options: Options) {
    if (!this.instance) {
      this.instance = new this(options);
    }

    return this.instance;
  }

  /**
   * Create a user
   * @param {UserPayload} [user] user payload
   */
  create(user: UserPayload) {
    return this.createEntity<UserCreateResponse, UserPayload>(
      Resource.User,
      user
    );
  }

  /**
   * Read a user by id
   * @param {Id} [user_id] user id
   */
  get(user_id: Id) {
    return this.getEntity<UserGetResponse>(Resource.User, user_id);
  }

  /**
   * Get a list of company users
   */
  list(params?: Record<string, any>) {
    return this.getEntities<UserListResponse>(Resource.Users, params);
  }

  /**
   * Get a filtered list of company users
   */
  autocomplete(query?: string) {
    const url = `${this.buildUrl(Resource.Users)}/autocomplete${query}`;
    return this.makeRequest<UserListResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * Update a user
   * @param {UserPayload} [user] user payload
   */
  update(user_id: Id, user: UserPayload) {
    return this.updateEntity<UserPayload>(Resource.User, user_id, user);
  }

  /**
   * Delete user profile
   * @param {Id} [user_id] user id
   */
  delete(user_id: Id) {
    return this.deleteEntity(Resource.User, user_id);
  }

  /**
   * Resend account link email
   * @param {Id} [user_id] user id
   */
  resendAccountLink(user_id: Id) {
    const url = `${this.buildUrl(Resource.User)}/${
      UserAction.ResendAccountLink
    }?id=${user_id}`;

    return this.makeRequest<SuccessResponse>({
      url,
      method: this._request.Post
    });
  }

  /**
   * Resend account invitation email
   * @param {Id} [user_id] user id
   */
  resendInvitation(user_id: Id) {
    const url = `${this.buildUrl(Resource.User)}/${
      UserAction.ResendInvitation
    }?id=${user_id}`;
    return this.makeRequest<SuccessResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * Bulk resend account invitation emails
   */
  bulkResendInvitation() {
    const url = `${this.buildUrl(Resource.Users)}/${
      UserAction.ResendInvitation
    }`;
    return this.makeRequest<SuccessResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * Unlink linked account
   * @param {Id} [user_id] user id
   */
  unlink(user_id: Id) {
    const url = `${this.buildUrl(Resource.User)}/${
      UserAction.Unlink
    }?id=${user_id}`;
    return this.makeRequest<SuccessResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * upload company users
   * @param {FormData} [payload] formdata with key `users_to_upload` and value as the file
   */
  upload(payload: FormData) {
    const url = `${this.buildUrl(Resource.Users)}/${UserAction.Upload}`;

    return this.makeRequest<UserUploadResponse>({
      url,
      method: this._request.Post,
      data: payload,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  /**
   * Bulk delete company users
   * @param {FormData} [payload] formdata with key `users_to_delete_by_email` and value as the file
   */
  bulkDelete(payload: FormData) {
    const url = `${this.buildUrl(Resource.Users)}/${UserAction.BuklDelete}`;

    return this.makeRequest<UserBulkDeleteResponse>({
      url,
      method: this._request.Delete,
      data: payload,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  /**
   * Export users
   */
  exportAll() {
    const url = `${this.buildUrl(Resource.Users)}/export`;
    return this.makeRequest<UsersExportResponse>({
      url,
      method: this._request.Get
    });
  }
}
